import * as React from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { Formik, Form, FieldArray, ErrorMessage } from 'formik';
import { FormLabel, Input, FormControlLabel, Checkbox, Chip, Box, Card, CardContent, Divider, Stepper, Step, StepLabel } from '@mui/material';
import { postApi, getApi } from 'views/services/api';
import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import moment from 'moment';
import PropTypes from 'prop-types';
import { FormControl, InputLabel } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import IconButton from '@mui/material/IconButton';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { multiRoomBookingSchema } from 'schema/multiRoomBookingSchema';

const roomCardStyle = (selected) => ({
  cursor: 'pointer',
  border: selected ? '2px solid #673ab7' : '1px solid #e0e0e0',
  backgroundColor: selected ? '#ede7f6' : '#fff',
  transition: 'all 0.2s ease',
  '&:hover': {
    boxShadow: 3,
    transform: 'scale(1.02)'
  }
});


const MultiRoomBooking = (props) => {
  const { open, handleClose } = props;
  const { t } = useTranslation();
  const today = new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD

  const [roomData, setRoomData] = useState([]);
  const [existingCustomerData, setExistingCustomerData] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [totalBookingAmount, setTotalBookingAmount] = useState(0);
  const [amount, setAmount] = useState('');
  const [extraBedsCharge, setExtraBedsCharge] = useState(0);
  const [primaryCustomer, setPrimaryCustomer] = useState(null);
  const hotel = JSON.parse(localStorage.getItem('hotelData'));

  const [initialValues, setInitialValues] = useState({
    checkInDate: new Date().toISOString().split('T')[0], // Use current date dynamically
    checkOutDate: '',
    advanceAmount: 0,
    totalAmount: 0,
    advancePaymentMethod: '',
    addBeds: false,
    noOfBeds: 0,
    perBedAmount: 500,
    rooms: [
      {
        roomNo: '',
        roomType: '',
        amount: 0,
        addBeds: false,
        noOfBeds: 0,
        perBedAmount: 500,
        extraBedsCharge: 0,
        customers: [
          {
            phoneNumber: '',
            firstName: '',
            lastName: '',
            email: '',
            idCardType: 'default',
            idcardNumber: '',
            idFile: null,
            address: ''
          }
        ]
      }
    ]
  });
  const [activeStep, setActiveStep] = useState(0);

const steps = [
  t('Booking Details'),
   t('Room Selection'),
  t('Primary Customer Details'),
  t('Payment & Summary')
];


  // Fetch available rooms
  const fetchRoomData = async () => {
    try {
      const response = await getApi(`api/room/viewallrooms/${hotel?.hotelId}`);
      const availableRooms = response?.data?.filter(room => room.bookingStatus === 'false');
      setRoomData(availableRooms);
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (open) {
      fetchRoomData();
      // Update initial values with current date when modal opens
      const currentDate = new Date().toISOString().split('T')[0];
      setInitialValues(prev => ({
        ...prev,
        checkInDate: currentDate,
        checkOutDate: '',
        advanceAmount: 0,
        totalAmount: 0
      }));
      setPrimaryCustomer(null); // Reset primary customer when modal opens
    }
  }, [open]);

  // Calculate total amount for all rooms
  const calculateTotalAmount = (values) => {
    let total = 0;
    values.rooms.forEach(room => {
      if (room.roomNo && room.amount) {
        const checkInDate = new Date(values.checkInDate);
        const checkOutDate = new Date(values.checkOutDate);
        const numberOfDays = Math.ceil((checkOutDate - checkInDate) / (1000 * 3600 * 24)) || 1;

        let roomAmount = room.amount * numberOfDays;

        if (room.addBeds && room.noOfBeds > 0) {
          roomAmount += room.noOfBeds * room.perBedAmount;
        }

        total += roomAmount;
      }
    });

    setTotalBookingAmount(total);
    setAmount(total);
    return total;
  };

  // Handle advance amount change
  const handleAdvanceAmountChange = (e, setFieldValue) => {
    const inputAdvanceAmount = parseFloat(e.target.value) || 0;

    if (inputAdvanceAmount >= 0 && inputAdvanceAmount <= totalBookingAmount) {
      setFieldValue('advanceAmount', inputAdvanceAmount);
      const remainingAmount = totalBookingAmount - inputAdvanceAmount;
      setFieldValue('totalAmount', remainingAmount);
    } else if (inputAdvanceAmount > totalBookingAmount) {
      setFieldValue('advanceAmount', totalBookingAmount);
      setFieldValue('totalAmount', 0);
    } else {
      setFieldValue('advanceAmount', 0);
      setFieldValue('totalAmount', totalBookingAmount);
    }
  };

  // Handle checkbox change
  const handleCheckboxChange = (e) => {
    const isChecked = e.target.checked;
    setInitialValues((prevValues) => {
      const updatedValues = { ...prevValues, addBeds: isChecked, noOfBeds: isChecked ? prevValues.noOfBeds : 0 };
      calculateTotalAmount(updatedValues);
      return updatedValues;
    });
  };

  // Handle beds change
  const handleBedsChange = (e) => {
    const newNumberOfBeds = parseInt(e.target.value);
    setInitialValues((prevValues) => {
      const updatedValues = { ...prevValues, noOfBeds: newNumberOfBeds };
      calculateTotalAmount(updatedValues);
      return updatedValues;
    });
  };

  // Handle beds extra charge
  const handleBedsExtraCharge = (e) => {
    const newChages = parseInt(e.target.value || 0);
    setInitialValues((prevValues) => {
      const updatedValues = { ...prevValues, perBedAmount: newChages };
      calculateTotalAmount(updatedValues);
      return updatedValues;
    });
  };

  // Fetch customer data
  const fetchCustomerData = async (phoneNumber) => {
    try {
      const response = await getApi(`api/customer/view/${phoneNumber}?hotelId=${hotel?.hotelId}`);
      setExistingCustomerData(response?.data?.customerData[0]);
      return response?.data?.customerData[0];
    } catch (error) {
      console.log(error);
    }
  };

  // Handle room selection
  const handleRoomSelection = (roomNo, values, setFieldValue, roomIndex) => {
    const selectedRoom = roomData.find(room => room.roomNo === roomNo);
    if (selectedRoom) {
      setFieldValue(`rooms.${roomIndex}.roomNo`, roomNo);
      setFieldValue(`rooms.${roomIndex}.roomType`, selectedRoom.roomType);
      setFieldValue(`rooms.${roomIndex}.amount`, selectedRoom.amount);

      // Update total amount
      const updatedValues = {
        ...values,
        rooms: values.rooms.map((room, index) =>
          index === roomIndex
            ? { ...room, roomNo, roomType: selectedRoom.roomType, amount: selectedRoom.amount }
            : room
        )
      };
      calculateTotalAmount(updatedValues);

      // Reset advance amount when room selection changes
      setFieldValue('advanceAmount', 0);
      setFieldValue('totalAmount', 0);
    }
  };

  // Handle extra beds for a specific room
  const handleExtraBedsChange = (roomIndex, values, setFieldValue) => {
    const room = values.rooms[roomIndex];
    const checkInDate = new Date(values.checkInDate);
    const checkOutDate = new Date(values.checkOutDate);
    const numberOfDays = Math.ceil((checkOutDate - checkInDate) / (1000 * 3600 * 24)) || 1;

    let roomAmount = room.amount * numberOfDays;
    let extraBedsCharge = 0;

    if (room.addBeds && room.noOfBeds > 0) {
      extraBedsCharge = room.noOfBeds * room.perBedAmount;
      roomAmount += extraBedsCharge;
    }

    setFieldValue(`rooms.${roomIndex}.extraBedsCharge`, extraBedsCharge);

    // Update total amount
    const updatedValues = {
      ...values,
      rooms: values.rooms.map((r, index) =>
        index === roomIndex
          ? { ...r, extraBedsCharge }
          : r
      )
    };
    calculateTotalAmount(updatedValues);

    // Reset advance amount when extra beds change
    setFieldValue('advanceAmount', 0);
    setFieldValue('totalAmount', 0);
  };

  // Update all rooms with primary customer data
  const updateAllRoomsWithCustomerData = (customerData, setFieldValue, values) => {
    const updatedRooms = values.rooms.map(room => ({
      ...room,
      customers: [customerData]
    }));

    setFieldValue('rooms', updatedRooms);
    setPrimaryCustomer(customerData);
  };

  // Submit booking
  const handleSubmit = async (values, { resetForm, setSubmitting }) => {
    try {
      console.log('Multi-room booking values:', values);

      // Validate that all rooms have customers
      const hasEmptyCustomers = values.rooms.some(room =>
        room.customers.some(customer => !customer.firstName || !customer.phoneNumber)
      );

      if (hasEmptyCustomers) {
        toast.error('Please fill in customer details for all rooms');
        return;
      }

      // Validate that payment method is selected
      if (!values.advancePaymentMethod) {
        toast.error('Please select a payment method');
        return;
      }

      // Additional date validation
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const checkInDate = new Date(values.checkInDate);
      checkInDate.setHours(0, 0, 0, 0);

      const checkOutDate = new Date(values.checkOutDate);
      checkOutDate.setHours(0, 0, 0, 0);

      if (checkInDate < today) {
        toast.error('Check-in date cannot be in the past');
        return;
      }

      if (checkOutDate < checkInDate) {
        toast.error('Check-out date must be after check-in date..?');
        return;
      }

      // Prepare data for multi-room booking API
      const formData = new FormData();
      formData.append('hotelId', hotel.hotelId);
      formData.append('checkInDate', values.checkInDate);
      formData.append('checkOutDate', values.checkOutDate);
      formData.append('advanceAmount', values.advanceAmount);
      formData.append('advancePaymentMethod', values.advancePaymentMethod);

      // Prepare rooms data
      const roomsData = values.rooms.map((room, roomIndex) => {
        console.log(`Processing room ${roomIndex}:`, room);
        const roomData = {
          roomNo: room.roomNo,
          roomType: room.roomType,
          amount: room.amount,
          addBeds: room.addBeds,
          noOfBeds: room.noOfBeds,
          perBedAmount: room.perBedAmount,
          extraBedsCharge: room.extraBedsCharge,
          customers: room.customers.map((customer, customerIndex) => {
            console.log(`Processing customer ${customerIndex} in room ${roomIndex}:`, customer);
            const customerData = {
              phoneNumber: customer.phoneNumber,
              firstName: customer.firstName,
              lastName: customer.lastName,
              email: customer.email,
              idCardType: customer.idCardType,
              idcardNumber: customer.idcardNumber,
              address: customer.address
            };

            // Add file to formData if present
            if (customer.idFile && customer.idFile instanceof File) {
              console.log(`Adding file for room ${roomIndex}, customer ${customerIndex}:`, customer.idFile.name);
              formData.append(`idFile_${roomIndex}_${customerIndex}`, customer.idFile);
            } else {
              console.log(`No file for room ${roomIndex}, customer ${customerIndex}`);
            }

            return customerData;
          })
        };
        return roomData;
      });

      formData.append('rooms', JSON.stringify(roomsData));

      console.log('Sending request to API...');
      console.log('Rooms data being sent:', roomsData);
      console.log('FormData entries:');
      for (let [key, value] of formData.entries()) {
        console.log(key, value);
      }

      // Call the multi-room booking API
      console.log('Making API call to multiroombooking...');
      const response = await postApi('api/room/multiroombooking', formData);

      console.log('API Response:', response);
      console.log('Response status:', response.status);
      console.log('Response data:', response.data);

      if (response.status === 200 && response.data.success) {
        setActiveStep(0)
        toast.success(response.data.message || 'Multi-room reservations created successfully!');
        handleClose();
        resetForm();
        setSelectedRooms([]);
        // Refresh the parent component to update room data
        if (props.onBookingSuccess) {
          props.onBookingSuccess();
        }
      } else {
        toast.error(response?.response?.data?.message || 'Failed to create reservations. Please try again.');
      }
    } catch (error) {
      console.error('Error in multi-room booking:', error);
      console.error('Error response:', error.response);
      console.error('Error message:', error.message);
      if (error.response?.data?.message) {
        console.error('API Error message:', error.response.data.message);
        toast.error(error.response.data.message);
      } else if (error.message) {
        console.error('Error message:', error.message);
        toast.error(error.message);
      } else {
        console.error('Unknown error');
        toast.error('Failed to book rooms. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: '80vh'
        }
      }}
    >
      <DialogTitle sx={{
        backgroundColor: '#673ab7',
        color: 'white',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center'
      }}>
        <Typography variant="h6" sx={{color:"white"}}>{t('Multi-Room Booking')}</Typography>
        <Typography variant="subtitle1" sx={{color:"white"}}>
          {t('Total Amount')}: {hotel?.currencyCode} {totalBookingAmount}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        <Formik
          initialValues={initialValues}
          validationSchema={multiRoomBookingSchema}
          enableReinitialize={true}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, handleChange, handleSubmit, errors, touched, isValid, dirty, isSubmitting }) => (
            <Form encType="multipart/form-data" style={{ width: '100%', minHeight: '75vh',display: 'flex', flexDirection: 'column',justifyContent:"space-between" }}>
<div>
  
  {/* ================= STEPPER ================= */}
  <Stepper activeStep={activeStep} sx={{ mt:5,mb: 3 }}>
    {steps.map((label) => (
      <Step key={label}>
        <StepLabel>{label}</StepLabel>
      </Step>
    ))}
  </Stepper>


  {/* ================= STEP 1 : PRIMARY CUSTOMER ================= */}
  {activeStep === 0 && (
    <Card sx={{ mb: 3, p: 2 }}>
               <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <FormLabel>{t('Check-in Date')}</FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      name="checkInDate"
                      value={values.checkInDate}
                      onChange={(e) => {
                        handleChange(e);
                        calculateTotalAmount({ ...values, checkInDate: e.target.value });
                        // Reset advance amount when dates change
                        setFieldValue('advanceAmount', 0);
                        setFieldValue('totalAmount', 0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: new Date().toISOString().split('T')[0] }}
                      error={errors.checkInDate && Boolean(errors.checkInDate)}
                      helperText={touched.checkInDate && errors.checkInDate}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormLabel>{t('Check-out Date')}</FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="date"
                      name="checkOutDate"
                      value={values.checkOutDate}
                      onChange={(e) => {
                        handleChange(e);
                        calculateTotalAmount({ ...values, checkOutDate: e.target.value });
                        // Reset advance amount when dates change
                        setFieldValue('advanceAmount', 0);
                        setFieldValue('totalAmount', 0);
                      }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{
                        min: values.checkInDate || new Date().toISOString().split('T')[0]
                      }}
                      error={errors.checkOutDate && Boolean(errors.checkOutDate)}
                      helperText={touched.checkOutDate && errors.checkOutDate}
                    />
                  </Grid>
                </Grid>
    </Card>
  )}

  {/* ================= STEP 2 : ROOM SELECTION ================= */}
{activeStep === 1 && (
  <div style={{ margin: '20px 0' }}>
    <FieldArray name="rooms">
      {({ push, remove }) => (
        <>
          {values.rooms.map((room, roomIndex) => (
            <Card
              key={roomIndex}
              sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0' }}
            >
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2
                }}
              >
                <Typography variant="h6" sx={{ color: '#673ab7' }}>
                  {t('Room')} {roomIndex + 1}
                </Typography>

                {values.rooms.length > 1 && (
                  <IconButton
                    onClick={() => remove(roomIndex)}
                    color="error"
                    size="small"
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Box>

              <Grid container spacing={2}>
                {/* ================= ROOM CARD GRID ================= */}
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                 {roomData
  .filter((availableRoom) => {
    // if no room selected → show all
    if (!room.roomNo) return true;

    // if selected → show only selected one
    return availableRoom.roomNo === room.roomNo;
  })
  .map((availableRoom) => {

                      const isSelected =
                        room.roomNo === availableRoom.roomNo;

                      return (
                        <Grid
                          item
                          xs={12}
                          sm={6}
                          md={2}
                          key={availableRoom.roomNo}
                        >
                          <Card
                            onClick={() => {
                              if (isSelected) {
                                // REMOVE selection
                                setFieldValue(
                                  `rooms.${roomIndex}.roomNo`,
                                  ''
                                );
                                setFieldValue(
                                  `rooms.${roomIndex}.roomType`,
                                  ''
                                );
                                setFieldValue(
                                  `rooms.${roomIndex}.amount`,
                                  0
                                );
                                calculateTotalAmount(values);
                              } else {
                                // SELECT room
                                handleRoomSelection(
                                  availableRoom.roomNo,
                                  values,
                                  setFieldValue,
                                  roomIndex
                                );
                              }
                            }}
                            sx={{
                              cursor: 'pointer',
                              border: isSelected
                                ? '2px solid #673ab7'
                                : '1px solid #e0e0e0',
                              backgroundColor: isSelected
                                ? '#ede7f6'
                                : '#fff',
                              transition: 'all 0.2s ease',
                              '&:hover': {
                                boxShadow: 3,
                                transform: 'scale(1.02)'
                              }
                            }}
                          >
                            <CardContent>
                              <Typography
                                variant="h6"
                                sx={{ fontWeight: 600 }}
                              >
                                {t('Room')} {availableRoom.roomNo}
                              </Typography>

                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {availableRoom.roomType}
                              </Typography>

                              <Typography
                                variant="subtitle1"
                                sx={{
                                  mt: 1,
                                  fontWeight: 'bold',
                                  color: '#673ab7'
                                }}
                              >
                                {hotel?.currencyCode}{' '}
                                {availableRoom.amount}
                              </Typography>

                              {isSelected && (
                                <Chip
                                  label={t('Selected')}
                                  color="primary"
                                  size="small"
                                  sx={{ mt: 1 }}
                                />
                              )}
                            </CardContent>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>

                {/* ================= ROOM DETAILS ================= */}
                {/* <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('Room Type')}
                    value={room.roomType}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label={t('Room Amount')}
                    value={`${hotel?.currencyCode} ${room.amount}`}
                    InputProps={{ readOnly: true }}
                  />
                </Grid> */}

                {/* ================= EXTRA BEDS ================= */}
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={room.addBeds}
                        onChange={(e) => {
                          setFieldValue(
                            `rooms.${roomIndex}.addBeds`,
                            e.target.checked
                          );
                          if (!e.target.checked) {
                            setFieldValue(
                              `rooms.${roomIndex}.noOfBeds`,
                              0
                            );
                            setFieldValue(
                              `rooms.${roomIndex}.extraBedsCharge`,
                              0
                            );
                          }
                          handleExtraBedsChange(
                            roomIndex,
                            values,
                            setFieldValue
                          );
                        }}
                      />
                    }
                    label={t('Add Extra Beds')}
                  />
                </Grid>

                {room.addBeds && (
                  <>
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('Number of Extra Beds')}
                        type="number"
                        value={room.noOfBeds}
                        onChange={(e) => {
                          setFieldValue(
                            `rooms.${roomIndex}.noOfBeds`,
                            parseInt(e.target.value) || 0
                          );
                          handleExtraBedsChange(
                            roomIndex,
                            values,
                            setFieldValue
                          );
                        }}
                        inputProps={{ min: 0, max: 5 }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label={t('Extra Beds Charge')}
                        value={`${hotel?.currencyCode} ${room.extraBedsCharge}`}
                        InputProps={{ readOnly: true }}
                      />
                    </Grid>
                  </>
                )}
              </Grid>
            </Card>
          ))}

          {/* ================= ADD ROOM ================= */}
          <Button
            startIcon={<AddIcon />}
            onClick={() =>
              push({
                roomNo: '',
                roomType: '',
                amount: 0,
                addBeds: false,
                noOfBeds: 0,
                perBedAmount: 500,
                extraBedsCharge: 0,
                customers: primaryCustomer
                  ? [primaryCustomer]
                  : [
                      {
                        phoneNumber: '',
                        firstName: '',
                        lastName: '',
                        email: '',
                        idCardType: 'default',
                        idcardNumber: '',
                        idFile: null,
                        address: ''
                      }
                    ]
              })
            }
            variant="contained"
            sx={{
              backgroundColor: '#673ab7',
              '&:hover': { backgroundColor: '#5e35b1' }
            }}
          >
            {t('Add Another Room')}
          </Button>
        </>
      )}
    </FieldArray>
  </div>
)}
  

  {/* ================= STEP 3 : PAYMENT & SUMMARY ================= */}
  {activeStep === 2 && (
    <>
            {/* Primary Customer Details */}
                <Card sx={{ mb: 3, p: 2 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#673ab7' }}>
                    {t('Primary Customer Details')}
                  </Typography>
                  <Typography variant="body2" color="textSecondary" gutterBottom>
                    {t('This customer information will be used for all rooms')}
                  </Typography>

                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('Phone Number')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        type="text"
                        placeholder="Enter Phone Number"
                        value={primaryCustomer?.phoneNumber || ''}
                        onChange={async (e) => {
                          const phoneNumber = e.target.value.replace(/\D/g, '').slice(0, 10);

                          // Update phone number in real-time
                          const updatedCustomer = {
                            ...primaryCustomer,
                            phoneNumber: phoneNumber
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);

                          // Only fetch customer data when we have exactly 10 digits
                          if (phoneNumber.length === 10) {
                            const customerData = await fetchCustomerData(phoneNumber);
                            let newCustomerData;

                            if (customerData) {
                              newCustomerData = {
                                phoneNumber,
                                firstName: customerData?.firstName || '',
                                lastName: customerData?.lastName || '',
                                email: customerData?.email || '',
                                idCardType: customerData?.idCardType || 'default',
                                idcardNumber: customerData?.idcardNumber || '',
                                address: customerData?.address || '',
                                idFile: customerData?.idFile || null
                              };
                            } else {
                              newCustomerData = {
                                phoneNumber,
                                firstName: '',
                                lastName: '',
                                email: '',
                                idCardType: 'default',
                                idcardNumber: '',
                                address: '',
                                idFile: null
                              };
                            }

                            setPrimaryCustomer(newCustomerData);
                            updateAllRoomsWithCustomerData(newCustomerData, setFieldValue, values);
                          }
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('First Name')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter First Name"
                        value={primaryCustomer?.firstName || ''}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            firstName: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('Last Name')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Last Name"
                        value={primaryCustomer?.lastName || ''}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            lastName: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('Email')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        type="email"
                        placeholder="Enter Email"
                        value={primaryCustomer?.email || ''}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            email: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('ID Card Type')}</FormLabel>
                      <Select
                        fullWidth
                        size="small"
                        value={primaryCustomer?.idCardType || 'default'}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            idCardType: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      >
                        <MenuItem value="default" disabled>
                          {t('Select ID Card Type')}
                        </MenuItem>
                        <MenuItem value="Aadharcard">{t('Aadhar Card')}</MenuItem>
                        <MenuItem value="VoterIdCard">{t('Voter ID Card')}</MenuItem>
                        <MenuItem value="PanCard">{t('Pan Card')}</MenuItem>
                        <MenuItem value="DrivingLicense">{t('Driving License')}</MenuItem>
                        <MenuItem value="passport">{t('Passport')}</MenuItem>
                        <MenuItem value="cinNo">{t('Cin No')}</MenuItem>
                      </Select>
                    </Grid>

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('ID Card Number')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter card number"
                        value={primaryCustomer?.idcardNumber || ''}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            idcardNumber: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      />
                    </Grid>

                    {existingCustomerData?.idFile ? (
                      <Grid item xs={12} md={6}>
                        <FormLabel>{t('ID Proof')}</FormLabel>
                        <Typography style={{ color: 'black', marginTop: '7px' }}>
                          <a
                            href={primaryCustomer?.idFile}
                            target="_blank"
                            rel="noreferrer"
                            style={{ textDecoration: 'none' }}
                          >
                            <Button startIcon={<VisibilityIcon />} variant="contained" color="primary">
                              {t('View ID Proof')}
                            </Button>
                          </a>
                        </Typography>
                      </Grid>
                    ) : (
                      <Grid item xs={12} md={6}>
                        <FormLabel>{t('Upload ID Card File')}</FormLabel>
                        <Input
                          type="file"
                          onChange={(event) => {
                            const updatedCustomer = {
                              ...primaryCustomer,
                              idFile: event.currentTarget.files[0]
                            };
                            setPrimaryCustomer(updatedCustomer);
                            updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                          }}
                          accept="image/*,.pdf"
                        />
                      </Grid>
                    )}

                    <Grid item xs={12} md={6}>
                      <FormLabel>{t('Address')}</FormLabel>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Enter Physical Address"
                        value={primaryCustomer?.address || ''}
                        onChange={(e) => {
                          const updatedCustomer = {
                            ...primaryCustomer,
                            address: e.target.value
                          };
                          setPrimaryCustomer(updatedCustomer);
                          updateAllRoomsWithCustomerData(updatedCustomer, setFieldValue, values);
                        }}
                      />
                    </Grid>
                  </Grid>
                </Card>
    </>
  )}
 {activeStep === 3 &&  (
  <div>
         <Grid container spacing={2} sx={{ mt: 2 }}>
                  <Grid item xs={12} md={6}>
                    <FormLabel>{t('Amount')}</FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      value={amount}
                      InputProps={{ readOnly: true }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormLabel>{t('Advance Amount')}</FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      placeholder="Enter advance amount"
                      value={values.advanceAmount || ''}
                      onChange={(e) => handleAdvanceAmountChange(e, setFieldValue)}
                      inputProps={{
                        min: 0,
                        max: totalBookingAmount,
                        step: 0.01
                      }}
                      error={touched.advanceAmount && Boolean(errors.advanceAmount)}
                      helperText={touched.advanceAmount && errors.advanceAmount}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormLabel>{t('Remaining Amount')}</FormLabel>
                    <TextField
                      fullWidth
                      size="small"
                      type="text"
                      value={`${hotel?.currencyCode} ${values.totalAmount || totalBookingAmount}`}
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiInputBase-input': {
                          color: values.totalAmount > 0 ? '#d32f2f' : '#2e7d32',
                          fontWeight: 'bold'
                        }
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <FormLabel>{t('Payment Method')}</FormLabel>
                      <Select
                        size="small"
                        name="advancePaymentMethod"
                        value={values.advancePaymentMethod}
                        onChange={(e) => {
                          setFieldValue('advancePaymentMethod', e.target.value);
                        }}
                        label={t('Payment Method')}
                        error={touched.advancePaymentMethod && Boolean(errors.advancePaymentMethod)}
                      >
                        <MenuItem value="UPI">{t('UPI')}</MenuItem>
                        <MenuItem value="Net Banking">{t('Net Banking')}</MenuItem>
                        <MenuItem value="Cash">{t('Cash')}</MenuItem>
                        <MenuItem value="Card">{t('Card')}</MenuItem>
                        <MenuItem value="mvola">{t('Mvola')}</MenuItem>
                        <MenuItem value="credit">{t('Credit')}</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="addBeds"
                          color="primary"
                          checked={initialValues.addBeds}
                          onChange={handleCheckboxChange}
                        />
                      }
                      label={t('Add Beds')}
                    />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Number of Beds')}
                      type="number"
                      disabled={!initialValues.addBeds}
                      value={initialValues.noOfBeds}
                      onChange={handleBedsChange}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      size="small"
                      label={t('Amount of Extra Bed')}
                      type="number"
                      disabled={!initialValues.addBeds}
                      value={initialValues.perBedAmount}
                      onChange={handleBedsExtraCharge}
                    />
                  </Grid>
                </Grid>
                {/* Calculation Summary */}
                {totalBookingAmount > 0 && (
                  <Card sx={{ mt: 2, p: 2, backgroundColor: '#f8f9fa' }}>
                    <Typography variant="h6" gutterBottom sx={{ color: '#673ab7' }}>
                      {t('Calculation Summary')}
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t('Total Room Charges')}
                        </Typography>
                        <Typography variant="h6" color="primary">
                          {hotel?.currencyCode} {totalBookingAmount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t('Advance Amount')}
                        </Typography>
                        <Typography variant="h6" color="success.main">
                          {hotel?.currencyCode} {values.advanceAmount || 0}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t('Remaining Amount')}
                        </Typography>
                        <Typography variant="h6" color="error.main">
                          {hotel?.currencyCode} {values.totalAmount || totalBookingAmount}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Typography variant="subtitle2" color="textSecondary">
                          {t('Payment Status')}
                        </Typography>
                        <Typography
                          variant="h6"
                          color={values.advanceAmount >= totalBookingAmount ? 'success.main' : 'warning.main'}
                        >
                          {values.advanceAmount >= totalBookingAmount ? t('Fully Paid') : t('Partial Payment')}
                        </Typography>
                      </Grid>
                    </Grid>

                                                <Divider sx={{ my: 2 }} />

                            <Typography variant="subtitle1" gutterBottom sx={{ color: '#673ab7' }}>
                              {t('Customer Details')} - {t('Room')}
                            </Typography>

                            <Typography variant="body2" color="textSecondary" gutterBottom>
                              {t('Using primary customer information')}
                            </Typography>

                            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
                              <Typography variant="body2">
                                <strong>{t('Name')}:</strong> {primaryCustomer?.firstName} {primaryCustomer?.lastName}
                              </Typography>
                              <Typography variant="body2">
                                <strong>{t('Phone')}:</strong> {primaryCustomer?.phoneNumber}
                              </Typography>
                              <Typography variant="body2">
                                <strong>{t('Email')}:</strong> {primaryCustomer?.email || t('Not provided')}
                              </Typography>
                              <Typography variant="body2">
                                <strong>{t('ID')}:</strong> {primaryCustomer?.idCardType !== 'default' ? `${primaryCustomer?.idCardType}: ${primaryCustomer?.idcardNumber}` : t('Not provided')}
                              </Typography>
                            </Box>
                          {/* Validation Summary */}
              {Object.keys(errors).length > 0 && (
                <Card sx={{ mt: 2, p: 2, backgroundColor: '#ffebee', border: '1px solid #f44336' }}>
                  <Typography variant="h6" gutterBottom sx={{ color: '#d32f2f' }}>
                    {t('Please fix the following errors:')}
                  </Typography>
                  <ul style={{ margin: 0, paddingLeft: '20px' }}>
                    {errors.checkInDate && (
                      <li style={{ color: '#d32f2f' }}>{t('Check-in Date')}: {errors.checkInDate}</li>
                    )}
                    {errors.checkOutDate && (
                      <li style={{ color: '#d32f2f' }}>{t('Check-out Date')}: {errors.checkOutDate}</li>
                    )}
                    {errors.advanceAmount && (
                      <li style={{ color: '#d32f2f' }}>{t('Advance Amount')}: {errors.advanceAmount}</li>
                    )}
                    {errors.advancePaymentMethod && (
                      <li style={{ color: '#d32f2f' }}>{t('Payment Method')}: {errors.advancePaymentMethod}</li>
                    )}
                    {errors.rooms && Array.isArray(errors.rooms) && errors.rooms.map((roomErrors, roomIndex) => {
                      if (roomErrors && typeof roomErrors === 'object') {
                        return Object.keys(roomErrors).map(key => {
                          if (key === 'customers' && Array.isArray(roomErrors.customers)) {
                            return roomErrors.customers.map((customerErrors, customerIndex) => {
                              if (customerErrors && typeof customerErrors === 'object') {
                                return Object.keys(customerErrors).map(customerKey => {
                                  if (customerErrors[customerKey]) {
                                    return (
                                      <li key={`${roomIndex}-${customerIndex}-${customerKey}`} style={{ color: '#d32f2f' }}>
                                        {t('Room')} {roomIndex + 1}, {t('Customer')} {customerIndex + 1}, {t(customerKey)}: {customerErrors[customerKey]}
                                      </li>
                                    );
                                  }
                                  return null;
                                });
                              }
                              return null;
                            });
                          }
                          if (roomErrors[key]) {
                            return (
                              <li key={`${roomIndex}-${key}`} style={{ color: '#d32f2f' }}>
                                {t('Room')} {roomIndex + 1}, {t(key)}: {roomErrors[key]}
                              </li>
                            );
                          }
                          return null;
                        });
                      }
                      return null;
                    })}
                  </ul>
                </Card>
              )}
                  </Card>
                )}
  </div>
 )}

</div>
 
  {/* ================= FOOTER ================= */}
  <DialogActions  sx={{ p: 3}}>
    {activeStep > 0 && (
      <Button onClick={() => setActiveStep((s) => s - 1)}>
        {t('Back')}
      </Button>
    )}

    {activeStep < 3 && (
      <Button
        variant="contained"
        onClick={() => setActiveStep((s) => s + 1)}
        sx={{ backgroundColor: '#673ab7' }}
      >
        {t('Next')}
      </Button>
    )}

    {activeStep === 3 && (
      <Button
        type="submit"
        variant="contained"
        disabled={!isValid || !dirty || isSubmitting}
        sx={{
          backgroundColor: '#673ab7',
          '&:hover': { backgroundColor: '#5e35b1' }
          
        }}
      >
        {isSubmitting ? t('Booking...') : t('Book All Rooms')}
      </Button>
    )}
  </DialogActions>

</Form>

      
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

MultiRoomBooking.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onBookingSuccess: PropTypes.func
};

export default MultiRoomBooking;
